import { uniq } from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { getActionLog, getTimestamp, logAction, logEvent } from '../common/actionLog';
import {
  createBufferedEncoder, encodeCoord, encodePressure, moveBufferedEncoder, readBufferedEncoderBuffer,
  startBufferedEncoder
} from '../common/compressor';
import { CHAT_MESSAGE_LENGTH_LIMIT, DEFAULT_PRESENTATION_MODE_STATE, HOUR, KB, MAX_CHUNKED_DATA_SIZE, PENCIL_LOG, PRESENTATION_INACTIVE_HOST_MODAL_TIME } from '../common/constants';
import { signInToView, socketOptions } from '../common/data';
import {
  AccountData, Analytics, Announcement, ApplyFunc, CancellablePromise, CreateDrawingData,
  Drawing, IChatMessage, IConnectionService, IPublicServer, IServer, ToolSlot, IToolData, IToolModel, LayerLoadingResult,
  OtherAction, QuickAction, Rect, SelectionMode, SocketWithDrawings, ToolId, UpdateDrawingData, User, UserAction,
  Viewport, LoadingResult, DrawingLoadFailure, PresentationModeState, ClientBrowserInfo, CommandParams, ToolSource, PresentationModeStartState, PresentationActions, InvitePresentationViewers, PresentViewport, Feature,
} from '../common/interfaces';
import { isMaskEmpty } from '../common/mask';
import { defaultSettings } from '../common/settings';
import { toolIdToString } from '../common/toolIdUtils';
import { createAndInitUser, createUser, getTransformedSelectionBounds, resetUser } from '../common/user';
import { cloneBuffer, findByLocalId, getPixelRatio, hasKeyboard, isMobile } from '../common/utils';
import { deferred, Deferred, delay, parallel } from '../common/promiseUtils';
import { includes } from '../common/baseUtils';
import { usingWasm } from '../common/wasmUtils';
import { clearAllUsers, ClientActions, toHistory, toWait } from './clientActions';
import { cancelTool, Editor, hasLoadedDrawing, initEditorBrushesAndShapes, lockEditor, reset } from './editor';
import { ErrorReporter } from './errorReporter';
import { dismissChatHelp } from './help';
import { Modals } from './modals';
import { isActiveClientActions, Model } from './model';
import { loadSettings, saveSettings } from './settingsService';
import { uploadAvatar } from './uploadService';
import { VoiceChatService } from './voiceChatService';
import { undosLog } from '../common/history';
import { hasDrawingRole, hasPro } from '../common/userRole';
import { IThreadService } from './thread.service.interface';
import { initBrushesAndShapes } from '../common/shapes';
import { blobToArrayBuffer, canvasToBlob, dataToImage } from '../common/canvasUtils';
import { getLayer } from '../common/drawing';
import { pencilLog } from '../common/paintBrush';
import { ManageService } from './manageService';
import { storageGetBoolean, storageSetBoolean } from './storage';
import { getTabletModel, hasExtension, hasPressure } from './tablet';
import { CommandService } from './commandService';
import { getLastToolSource } from '../common/toolUtils';
import { isRectEmpty } from '../common/rect';
import { copyViewport, createViewport, fitViewportOnScreen, matchFromOtherViewport } from '../common/viewport';
import { navigateToDrawing } from '../common/clientUtils';
import { getToolFullName } from '../common/tools';

const MAX_ENCODER_FLUSH_SIZE = 1024;
const DATA_SEND_LIMIT = 128 * KB;
let reportedMissingLayer = false;
let reportsLeft = 5;
let nextDrawingConnId = 1;
const MAX_PRESENTATION_TIMEOUT = 4 * HOUR;

function createClientActions(socket: SocketWithDrawings, model: Model, applyCallback: ApplyFunc) {
  const client = new ClientActions(socket, model, applyCallback);
  const keys = uniq([...toHistory, ...toWait]) as (keyof ClientActions)[];

  for (const key of keys) {
    const method: any = (client[key] as any).bind(client);
    const hasHistory = toHistory.indexOf(key) !== -1;
    const hasWait = toWait.indexOf(key) !== -1;

    client[key] = function (...args: any[]) {
      const isActive = isActiveClientActions(model, client);

      if (hasHistory && model.sendingHistory) {
        if (isActive) model.drawingHistory.push(() => method(...args));
      } else if (hasWait && (model.toolPromise || model.waitingActions.length)) {
        if (isActive && key === 'disconnected') logAction('disconnected (waiting)');
        if (isActive && key === 'connected') logAction('connected (waiting)');
        if (isActive) model.waitingActions.push(() => method(...args));
      } else {
        return method(...args);
      }
    } as any;
  }

  return client;
}

export class RealModel implements IToolModel {
  typeName = 'Model';
  type = 'local';
  // errors / statuses
  connectionIssues = false;
  waitingBeforeOpening = false;
  promptReload = true;
  importing = false;
  importingProgress = 0;
  contextLost = false;
  error: string | undefined = undefined;
  rendererError: string | undefined = undefined;
  fatalError: string | undefined = undefined;
  announcement: Announcement | undefined = undefined;
  passwordNeeded = false;
  // state
  // isConnected = false;
  get isConnected() {
    return !!this.connectionService.sockets[0]?.initialized;
  }
  set isConnected(value) { // for testing
    if (TESTS && this.connectionService.sockets.length) {
      this.connectionService.sockets[0].initialized = value;
    }
  }
  isSleeping = false;
  loaded = 1;
  loadingFailed = false;
  failed = false;
  failedDrawingLoad = DrawingLoadFailure.None;
  sentTools = 0;
  confirmedTools = 0;
  drawingSamples = 0;
  longTask: string | undefined = undefined; // TODO: ...
  // users
  user = createUser('', 0);
  userChanged = new BehaviorSubject<User | undefined>(undefined);
  users: User[] = [];
  tempUsers: User[] = [];
  // chat
  message: IChatMessage | undefined = undefined;
  messages: IChatMessage[] = [];
  messageQueue: IChatMessage[] = [];
  showChatPrompt = false;
  // tool
  toolCounter = 1;
  toolStarted = false;
  encoder = createBufferedEncoder();
  nextToolFlushTime = 0;
  // history
  sendingHistory = false;
  drawingHistory: (() => void)[] = [];
  // action queue
  waitingActions: (() => void)[] = [];
  toolPromise: Promise<void> | undefined = undefined;
  insideCommitLoop = false;
  // drawing
  editor: Editor | undefined = undefined;
  openDrawingId: string | undefined = undefined;
  connId = 0;
  presentationModeState: PresentationModeState = DEFAULT_PRESENTATION_MODE_STATE;
  // settings
  settings = defaultSettings();
  lastSettings = defaultSettings();
  activeSlot: ToolSlot | undefined = undefined;
  // presentation
  presentationViewers: User[] = [];
  private _hideUI = false;
  get hideUI() {
    const enforceUiHidden = !this.isPresentationMode || this.isPresentationHost
      || !this.presentationModeState.participantsUiHidden;
    return enforceUiHidden ? this._hideUI : true;
  }
  set hideUI(value: boolean) {
    this._hideUI = value;
  }
  get disableSequenceStepChange() {
    return this.isPresentationMode && !this.isPresentationHost && this.presentationModeState.followingHostLocationEnforced;
  }
  settingsChanged = new Subject<void>();
  scheduledSettingsSave: any = 0;
  toolTitles = new Map<number, string>();
  commandTitles = new Map<string, string>();
  settingsInitialized = false;
  // other
  pressureApiSent = false;
  rendererApiSent = false;
  lastNewFeature = '';
  newFeature = '';
  pendingLayerOwns = new Set<number>();
  selectLayerOnOwn = 0;
  createdAccount = false;
  loadingPromise: CancellablePromise<LayerLoadingResult> | undefined = undefined;
  finishedLoadingDrawing = new Subject<Drawing>();
  connectedStateChanged = new BehaviorSubject(false);
  onLoaded: () => void = () => { }; // for testing
  onBeforeChatMessage = new Subject<void>();
  onUserError = new Subject<{ message: string; type?: 'danger' | 'success'; }>();
  closingPromise: Promise<void> | undefined = undefined;
  testPromise: Promise<void> | undefined = undefined; // for testing
  hideDebug = false;
  viewBeforeJoiningPresentationMode: { view: Viewport, drawingId: string } = { view: createViewport(), drawingId: '' };
  // storage
  storageLimitExceeded = false;
  get server() {
    return this.socket.server as IPublicServer;
  }
  get socket() {
    if (DEVELOPMENT && !this.connectionService.sockets.length) throw new Error('Missing at least one socket');
    return this.connectionService.sockets[0].socket;
  }
  socketFor(drawingId: string) {
    return this.connectionService.sockets.find(s => includes(s.drawings, drawingId));
  }
  get isAnyModalOpen() {
    return this.modals.isAnyModalOpen();
  }
  constructor(
    public connectionService: IConnectionService,
    private errorReporter: ErrorReporter,
    public modals: Modals,
    public voiceChat: VoiceChatService,
    public manage: ManageService,
    private commandService: CommandService,
    private applyCallback: ApplyFunc = f => f(),
    public threadService?: IThreadService,
  ) {
    loadSettings(this);

    this.user.name = 'Anonymous';

    this.voiceChat.model = this;

    this.connectionService.createClient = socket => createClientActions(socket, this, applyCallback);
    this.connectionService.onError = error => this.showError(error);
    this.connectionService.update();

    this.commandService.onError = (message, error) => this.handleError(message, error);

    if (typeof window !== 'undefined') {
      if (DEVELOPMENT) {
        (window as any).model = this;
      }

      window.onbeforeunload = () => {
        if (this.scheduledSettingsSave) {
          saveSettings(this);
        }
      };
    }

    // TEMP: debugging on mobile
    // (window as any).__onlog = (type: string, args: any[]) => this.server.debug('log', `[${type}] ${args.map(x => `${x}`).join(' ')}`);
  }
  private isAnySocketConnected() {
    return !!this.connectionService.sockets[0]?.socket?.isConnected;
  }
  reportError(message: string, error?: any | undefined, data?: any) {
    this.errorReporter.reportError(message, error, data);
  }
  internalReportError = (errorMessage: string, error: any, data: any) => {
    try {
      if (DEVELOPMENT) console.error('[reportError]', errorMessage, error, data);

      const msg = error?.message ?? '';
      const name = error?.name || 'Error';
      const message = error?.message || error?.stack || error || errorMessage || '';
      const stack = error?.stack || '';

      if (/^out[ _]of[ _]memory|Array buffer allocation failed|메모리 부족/i.test(msg)) {
        this.applyCallback(() => this.fatalError = 'Out of memory');
        this.editor && lockEditor(this.editor, 'out of memory');
      } else if (/GPU device instance has been suspended|^failed to create texture|^WebGL not initialized/i.test(msg)) {
        this.applyCallback(() => this.fatalError = 'Graphics card failure');
        this.editor && lockEditor(this.editor, 'gpu fail');
      }

      if (/Missing layer|No activeLayer/.test(message)) {
        if (reportedMissingLayer) return;
        reportedMissingLayer = true;
      }

      if (/Editor locked/.test(message) && this.fatalError === 'Out of memory') return;
      if (message === '0') return; // plyr error

      const renderer = this.editor?.renderer?.name || '';
      const usedMem = ((performance as any)?.memory?.usedJSHeapSize || 0) / (1024 * 1024 * 1024);
      const totalMem = ((performance as any)?.memory?.jsHeapSizeLimit || 0) / (1024 * 1024 * 1024);
      const wnd = typeof window !== 'undefined' ? window : undefined;
      let actions = `\n[${getTimestamp(Date.now())}] clientId: ${this.user.localId}\n` +
        `renderer: ${renderer}, wasm: ${usingWasm}, memory: ${usedMem.toFixed(2)}/${totalMem.toFixed(2)}GB\n` +
        `screen: ${wnd?.screen?.width}x${wnd?.screen?.height}, ratio: ${getPixelRatio()}\n` +
        `layers: [${this.editor?.drawing.layers.map(l => l.id).join(', ')}]\n` +
        `actions:\n${getActionLog().map(x => `  ${x}`).join('\n')}\n\n`;

      if (/Invalid packet/.test(message)) {
        actions += 'socketOptions: ' + socketOptions;
      }

      if ((message || stack || data) && (reportsLeft-- > 0)) {
        let text = message || '<unknown>';
        if (typeof text !== 'string') text = JSON.stringify(text);
        void this.send(s => s.error(name, text, stack, actions, data, errorMessage));
      }

      logAction(`error: ${message}`);
    } catch (e) {
      console.error('[socket.server.error]', e.stack);
    }
  };
  dismissError() {
    this.error = undefined;
    this.promptReload = true;
  }
  showError(error: string, promptReload = true, fatal = false) {
    this.applyCallback(() => {
      if (fatal) {
        this.fatalError = error;
      } else {
        this.error = error;
        this.promptReload = promptReload;
      }
    });
  }
  handleError(message: string, error: Error) {
    this.errorReporter.reportError(`handleError (${message})`, error);

    if (message === 'Array buffer allocation failed') {
      message = 'Out of memory';
    }

    this.showError(message);
  }
  async changeColor(color: string) {
    await this.server.changeColor(color);
  }
  async changeAvatar(file: File | Blob) {
    const name = await uploadAvatar(file);
    await this.server.changeAvatar(name);
  }
  async updateAccount(data: AccountData) {
    await this.server.updateAccount(data);
  }
  setTaskName(name: string) {
    this.applyCallback(() => this.longTask = name);
  }
  startTask(name: string) {
    const timeout = setTimeout(() => this.longTask = name, 500);

    return () => {
      clearTimeout(timeout);
      this.longTask = undefined;
    };
  }
  cancelLoading() {
    this.loadingPromise?.cancel();
    this.loadingPromise = undefined;
  }
  getSocketSent(): number {
    const socket = this.connectionService.sockets[0]?.socket;

    if (socket) {
      const size = socket.sentSize;
      this.socket.sentSize = 0;
      return size;
    } else {
      return 0;
    }
  }
  getSocketReceived(): number {
    const socket = this.connectionService.sockets[0]?.socket;

    if (socket) {
      const size = this.socket.receivedSize;
      this.socket.receivedSize = 0;
      return size;
    } else {
      return 0;
    }
  }
  canUseProBrushes() {
    return hasPro(this.user) || this.manage.hasToolTrial('brushes');
  }
  async signIn(email: string, password: string) {
    await this.socket.server.signIn(email, password);
    if (signInToView) this.reconnect();
  }
  async signOut() {
    await this.socket.server.signOut();

    if (signInToView) {
      await delay(1000);
      this.reconnect();
    }
  }
  // private opening = 0;
  async open(id: string | undefined) {
    try {
      logEvent(`open (id: ${id})`);

      // TODO: instead handle this correctly ?
      // if (this.opening) this.reportError('Called open() while opening', undefined, undefined);
      // this.opening++;

      if (id && this.isPresentationMode && this.isPresentationHost && this.presentationModeState.followingHostLocationEnforced) {
        this.server.quickAction(QuickAction.PresentDrawing, id);
      }

      if (this.closingPromise) {
        logAction('waiting for closingPromise');
        await this.closingPromise;
      }

      this.editor?.textTool.confirmDeferredChange();
      void this.waitForToolPromise(); // TODO: add await here

      this.editor?.filterCancelled.next();

      const prevId = this.openDrawingId;
      this.openDrawingId = id?.trim();
      this.nextConnId();

      logEvent(`conn (connId: ${this.connId})`);

      while (this.isConnected && !this.fatalError && this.editor?.drawingInProgress) {
        if (!this.waitingBeforeOpening) logAction('waitingBeforeOpening');
        this.waitingBeforeOpening = true;
        await delay(100);
      }

      this.waitingBeforeOpening = false;
      this.passwordNeeded = false;

      if (this.fatalError) return;

      if (this.editor) {
        logAction(`open:cancelLoading (id: ${prevId})`);
        this.cancelLoading();

        this.editor.activeTool?.cancelBeganTool?.();
        cancelTool(this.editor, 'open');
        lockEditor(this.editor, 'open');
        this.loaded = 0;
        this.modals.closeDrawingSettings();
        this.clearQueuedSends();
      }

      this.connectionService.drawingId = this.openDrawingId;
      this.connectionService.connId = this.connId;
      this.connectionService.update();
    } finally {
      // this.opening--;
    }
  }
  private nextConnId() {
    this.connId = nextDrawingConnId;
    nextDrawingConnId = (nextDrawingConnId + 1) % 0xff;
    if (!nextDrawingConnId) nextDrawingConnId++;
  }
  // TODO: remove this ?
  reconnect() {
    this.isSleeping = false;
    this.connectionService.reconnect();
  }
  close(): Promise<void> {
    return this.closingPromise = (this.closingPromise || Promise.resolve())
      .then(() => this.innerClose())
      .then(() => this.closingPromise = undefined);
  }
  async waitForToolPromise() {
    while (this.toolPromise) await this.toolPromise;
  }
  private async innerClose() {
    this.editor?.textTool.confirmDeferredChange();
    const id = this.editor?.drawing.id;
    logEvent(`close (drawingId: ${id})`);
    this.openDrawingId = undefined;
    this.cancelLoading();
    await this.waitForToolPromise();
    this.modals.closeDrawingSettings();

    if (this.editor) {
      while (this.editor.drawingInProgress) {
        await delay(100);
      }

      this.editor.activeTool?.cancelBeganTool?.();

      cancelTool(this.editor, 'close');
      lockEditor(this.editor, 'close');
    }

    // wait on tool promise so we don't try to operate on drawing that doesn't exist anymore
    if (this.toolPromise) {
      try {
        await this.toolPromise;
      } catch (e) {
        DEVELOPMENT && console.error(e);
      }
    }

    // TODO: wait for queued sends

    this.nextConnId();
    this.connectionService.drawingId = undefined;
    this.connectionService.connId = this.connId;
    this.connectionService.sequenceIds = [];
    this.connectionService.update();

    if (this.user) {
      resetUser(this.user, this.editor);
      clearAllUsers(this);
    }

    this.clearQueuedSends();

    this.editor && reset(this.editor);
    this.voiceChat.session = false;
    this.voiceChat.stop();

    logAction(`closed (drawing: ${id})`);
  }
  getUserByUniqId(uniqId: string, inSequence: boolean): User | undefined {
    if (this.user.uniqId === uniqId) {
      return this.user;
    }

    for (const user of this.users) {
      if (user.uniqId == uniqId) {
        return user;
      }
    }

    if (inSequence && this.editor?.drawing.sequence) {
      for (const drawing of this.editor.drawing.sequence) {
        for (const user of drawing.users) {
          if (user.uniqId == uniqId) {
            return user;
          }
        }
      }
    }

    return undefined;
  }
  getUserIdsByUniqId(user: User, inSequence: boolean): string[] {
    const userIds: string[] = [user.uniqId];

    for (const u of this.users) {
      if (u.name == user.name) {
        userIds.push(u.uniqId);
      }
    }

    if (inSequence && this.editor?.drawing.sequence) {
      for (const drawing of this.editor.drawing.sequence) {
        for (const inDrawingUser of drawing.users) {
          if (inDrawingUser.name == user.name) {
            userIds.push(inDrawingUser.uniqId);
          }
        }
      }
    }
    return userIds;
  }
  getUserByLocalId(localId: number): User | undefined {
    if (this.user.localId === localId) {
      return this.user;
    } else {
      return findByLocalId(this.users, localId);
    }
  }
  getUserOrTemp(localId: number): User | undefined {
    if (localId === 0) return undefined;

    let user = this.getUserByLocalId(localId) || findByLocalId(this.tempUsers, localId);

    if (!user) {
      user = createAndInitUser('', localId, undefined, this.editor);
      this.tempUsers.push(user);
    }

    return user;
  }
  reloadSession(token?: string) {
    try {
      if (this.socket.isConnected) {
        this.socket.server.otherAction(OtherAction.ReloadSession, token)
          .catch(e => DEVELOPMENT && console.error(e));
      }
    } catch (e) {
      DEVELOPMENT && console.error(e);
    }
  }
  async tryUserAction<T>(action: UserAction, localId = 0, param?: T) {
    if (this.isConnected) {
      await this.server.userAction(this.connId, action, localId, param);
    }
  }
  tryQuickAction<T>(action: QuickAction, param?: T) {
    if (this.isConnected) {
      this.server.quickAction(action, param);
    }
  }
  async createDrawing(data: CreateDrawingData) {
    return await this.server.createDrawing(data);
  }
  async updateDrawing(data: UpdateDrawingData) {
    if (this.isConnected && this.editor && hasLoadedDrawing(this.editor)) {
      await this.server.updateDrawing(this.connId, data);
    }
  }
  async importDrawing(name: string, data: Uint8Array, addToSequence = false) {
    this.importingProgress = 0.1;

    if (data.byteLength > MAX_CHUNKED_DATA_SIZE) throw new Error(`File is too large`);

    let info: Partial<CreateDrawingData> = { name };

    if (this.editor?.drawing.id) {
      const { team, project, folder } = this.editor.drawing;
      info = { ...info, team, project, folder };
    }

    if (addToSequence) {
      info.shareType = this.editor?.drawing.shareType;
      info.addToSequence = true;
      info.copyPermissions = true;
    }

    const result = await this.sendAsync(async server => {
      const dataId = await this.sendDataInChunks(data, progress => this.importingProgress = 0.1 + progress * 0.8);
      return await server.importDrawing(info, undefined, dataId);
    });
    if ('error' in result) throw new Error(result.error);

    return result.id;
  }
  async sendLayerSnapshot(layerId: number, data: Uint8Array) {
    await this.sendAsync(async s => {
      const dataId = await this.sendDataInChunks(data);
      s.layerSnapshot(layerId, dataId);
    });
  }
  ownLayer(layerId: number): Promise<void> {
    const source = getLastToolSource();
    const connId = this.connId;
    return this.sendPromise(s => s.ownLayer(connId, layerId, source));
  }
  disownLayer(layerId: number): Promise<void> {
    const source = getLastToolSource();
    const connId = this.connId;
    return this.sendPromise(s => s.disownLayer(connId, layerId, source));
  }
  getNewLayerIds(count: number): Promise<number[]> {
    const connId = this.connId;
    return this.sendPromise(s => s.getNewLayerIds(connId, count));
  }
  private setupSelection(tool: IToolData) {
    if (tool.selection === undefined && !isMaskEmpty(this.user.selection)) {
      tool.selection = SelectionMode.Keep;
    }
  }
  private setToolFields(tool: IToolData) {
    tool.t = this.toolCounter++;
    tool.ts = getLastToolSource();

    const selectionBounds = getTransformedSelectionBounds(this.user); // this is expensive in some cases
    tool.s = isRectEmpty(selectionBounds) ? 0 : 1;
    if (tool.s && !tool.as) tool.as = selectionBounds;
  }
  doTool(layerId: number, tool: IToolData, data?: Uint8Array) {
    if (this.isConnected) {
      const connId = this.connId;
      this.checkLoaded();
      this.setToolFields(tool);
      this.flushTool();
      this.setupSelection(tool);
      logAction(`[local] > doTool: ${getToolFullName(tool)} (layerId: ${layerId}, sel: ${tool.selection}, t: ${tool.t})`);
      void this.send(s => s.doTool(connId, layerId, tool, data, 0));
      this.sentTools++;
    }
  }
  beginTool(layerId: number, tool: IToolData) {
    if (this.isConnected) {
      const connId = this.connId;
      this.checkLoaded();
      this.setToolFields(tool);
      this.toolStarted = true;
      this.flushTool();
      this.setupSelection(tool);
      logAction(`[local] > beginTool: ${toolIdToString(tool.id)} (sel: ${tool.selection}, t: ${tool.t})`);
      void this.send(s => s.beginTool(connId, layerId, tool));
      this.sentTools++;
    }
  }
  updateTool(layerId: number, tool: IToolData, update: any) {
    if (this.isConnected) {
      const connId = this.connId;
      this.checkLoaded();
      this.setToolFields(tool);
      this.flushTool();
      this.setupSelection(tool);
      logAction(`[local] > updateTool: ${toolIdToString(tool.id)} (sel: ${tool.selection}, t: ${tool.t})`);
      void this.send(s => s.updateTool(connId, layerId, tool, update));
      this.sentTools++;
    }
  }
  finishTool(layerId: number, tool: IToolData) {
    if (this.isConnected) {
      const connId = this.connId;
      this.checkLoaded();
      this.setToolFields(tool);
      this.toolStarted = false;
      this.flushTool();
      this.setupSelection(tool);
      logAction(`[local] > finishTool: ${toolIdToString(tool.id)} (sel: ${tool.selection}, t: ${tool.t})`);
      void this.send(s => s.finishTool(connId, layerId, tool));
      this.sentTools++;
    }
  }

  // ownership of `data` is passed to the function, do not pass mutable buffers
  doToolWithData(layerId: number, tool: IToolData, data: Uint8Array) {
    return this.internalDoToolWithData(layerId, tool, data)
      .catch(e => {
        if (e.message !== 'Cancelled') {
          this.errorReporter.reportError(e.message, e);
          this.showError(e.message);
        }
      });
  }
  errorWithData(message: string, info: string, data: Uint8Array) {
    logAction(`errorWithData (message: ${message})`);
    this.sendAsync(async s => {
      const dataId = await this.sendDataInChunks(data);
      s.errorWithData(message, info, dataId);
    }).catch(e => DEVELOPMENT && console.error(e));
  }
  private async internalDoToolWithData(layerId: number, tool: IToolData, data: Uint8Array) {
    if (this.isConnected) {
      if (data.byteLength > MAX_CHUNKED_DATA_SIZE && !this.user.isSuperAdmin) {
        throw new Error(`Data exceeds transfer limits`);
      }

      const connId = this.connId;
      this.checkLoaded();
      this.setToolFields(tool);
      this.flushTool();
      this.setupSelection(tool);
      logAction(`[local] > doToolWithData: ${toolIdToString(tool.id)} (layerId: ${layerId}, sel: ${tool.selection}, data: ${data.byteLength}, t: ${tool.t}|${tool.s})`);

      if (data.byteLength > DATA_SEND_LIMIT) {
        await this.sendAsync(async s => {
          const dataId = await this.sendDataInChunks(data);
          logAction(`[local] > doToolWithData: doTool(dataId)`);
          s.doTool(connId, layerId, tool, undefined, dataId);
        });
      } else {
        void this.send(s => {
          logAction(`[local] > doToolWithData: doTool(data)`);
          s.doTool(connId, layerId, tool, data, 0);
        });
      }

      logAction(`[local] > doToolWithData: complete`);
      this.sentTools++;
    }
  }
  startToolView(layerId: number, view: Viewport, tool: IToolData, xx: number, yy: number, p: number) {
    if (this.isConnected) {
      const connId = this.connId;
      this.checkLoaded();
      this.setToolFields(tool);
      this.flushTool();
      this.setupSelection(tool);
      startBufferedEncoder(this.encoder, xx, yy, encodePressure(p));
      logAction(`[local] > startToolView: ${toolIdToString(tool.id)} (layerId: ${layerId}, t: ${tool.t}|${tool.s})`);
      const { x, y, scale, rotation, flipped } = view; // we're taking snapshot of view here
      void this.send(s => s.startToolView(connId, layerId, x, y, scale, rotation, flipped, tool, encodeCoord(xx), encodeCoord(yy), encodePressure(p)));
      this.toolStarted = true;
      this.drawingSamples = 0;
    }
  }
  nextTool(x: number, y: number, p: number) {
    if (this.isConnected && this.toolStarted) {
      this.drawingSamples++;
      this.checkLoaded();
      moveBufferedEncoder(this.encoder, x, y, encodePressure(p));

      if (this.encoder.offset > MAX_ENCODER_FLUSH_SIZE) {
        this.flushTool();
      } else {
        this.nextToolFlushTime = this.nextToolFlushTime || performance.now();
      }
    }
  }
  endTool(
    toolId: ToolId, x: number, y: number, p: number, beforeRect: Rect, afterRect: Rect,
    startX: number, startY: number, endX: number, endY: number
  ) {
    if (this.isConnected && this.toolStarted) {
      const connId = this.connId;
      this.checkLoaded();
      this.flushTool();
      logAction(`[local] > endTool: ${toolIdToString(toolId)}`);
      const bx = beforeRect.x;
      const by = beforeRect.y;
      const bw = beforeRect.w;
      const bh = beforeRect.h;
      const ax = afterRect.x;
      const ay = afterRect.y;
      const aw = afterRect.w;
      const ah = afterRect.h;
      void this.send(s => s.endTool(connId, toolId, encodeCoord(x), encodeCoord(y), encodePressure(p), bx, by, bw, bh, ax, ay, aw, ah, startX, startY, endX, endY));
      this.sentTools++;
    }

    this.toolStarted = false;
    this.drawingSamples = 0;
  }
  cancelTool(message: string) {
    if (this.isConnected && this.toolStarted) {
      const connId = this.connId;
      this.checkLoaded();
      this.flushTool();
      logAction(`[local] > cancelTool (${message})`);
      void this.send(s => s.cancelTool(connId, message));
    }

    this.toolStarted = false;
    this.drawingSamples = 0;
  }
  private checkLoaded() {
    if (this.loaded !== 1) throw new Error('Not loaded');
    if (!this.editor?.drawing.id) throw new Error('Not loaded (id)');
    if (this.editor?.locked) throw new Error(`Editor locked (${this.editor.lockedReason})`);
  }
  flushTool() {
    if (this.isConnected && this.encoder.offset) {
      // TODO: wait here, clone buffer only if sending asynchronously
      const data = cloneBuffer(readBufferedEncoderBuffer(this.encoder));
      const connId = this.connId;
      void this.send(s => s.nextToolArray(connId, data));
      this.nextToolFlushTime = 0;
    }
  }
  selectLayer(layerId: number) {
    const connId = this.connId;
    void this.send(s => s.selectLayer(connId, layerId));
  }
  reorderLayers(order: number[]) {
    const connId = this.connId;
    void this.send(s => s.reorderLayers(connId, order));
  }
  undo(t: number) {
    const connId = this.connId;
    void this.send(s => s.undo(connId, t, getLastToolSource()));
  }
  redo(t: number) {
    const connId = this.connId;
    void this.send(s => s.redo(connId, t, getLastToolSource()));
  }
  loadedDrawing(connId: number, result: LoadingResult) {
    void this.send(s => s.loadedDrawing(connId, result));

    // TEMP: log for investigating missing undos (of auto-generated add layer)
    if (result === LoadingResult.Finished) {
      void this.send(s => s.debug('loaded', this.users.map(u => `(clientId: ${u.localId}, undos: ${undosLog(u.history)})`).join('\n')));
    }
  }
  // Queued packet sending
  private dataId = 0;
  private clearSendingChunksId = 0;
  async sendDataInChunks(data: Uint8Array, onProgress: (progress: number) => void = () => { }) {
    const id = ++this.dataId;
    const chunkSize = DATA_SEND_LIMIT;
    const chunks: { offset: number; chunk: Uint8Array; }[] = [];
    const size = data.byteLength;
    const clearSendingChunksId = this.clearSendingChunksId;

    for (let offset = 0; offset < size; offset += chunkSize) {
      const end = Math.min(size, offset + chunkSize);
      const chunk = data.subarray(offset, end);
      chunks.push({ offset, chunk });
    }

    let first = true;

    do {
      let progress = 0;
      if (!first) logAction(`[local] chunk restart`);
      first = false;

      try {
        await parallel(chunks, async ({ offset, chunk }) => {
          let resend = false;

          while (true) {
            if (!this.isConnected || clearSendingChunksId !== this.clearSendingChunksId) throw new Error('Cancelled');

            const rand = (Math.random() * 0xffffffff) >>> 0;
            logAction(`[local] chunk ${offset}-${offset + chunk.byteLength} of ${size}${resend ? ' (re-send)' : ''}`);
            const result = await this.socket.server.dataChunk(id, size, offset, chunk, rand);
            if (result.offset === offset && result.length === chunk.byteLength) break;
            resend = true;
          }

          progress++;
          onProgress(progress / chunks.length);
        }, 2);
      } catch (e) {
        // catch Disconnected error from dataChunk
        if (clearSendingChunksId !== this.clearSendingChunksId) throw new Error('Cancelled');
        throw e;
      }

      // TODO: remove throw if we remove dataChunksDone
      if (!this.isConnected || clearSendingChunksId !== this.clearSendingChunksId) throw new Error('Cancelled');
      // TODO: do we need this anymore
      // re-try if failed to send data
    } while (!await this.socket.server.dataChunksDone());

    if (clearSendingChunksId !== this.clearSendingChunksId) throw new Error('Cancelled');

    return id;
  }
  sending = false;
  private sendingQueue: { action: (server: IServer) => any; defer: Deferred<any>; }[] = [];
  private async send(action: (server: IServer) => void): Promise<void> {
    if (this.sending) {
      const defer = deferred();
      this.sendingQueue.push({ action, defer });
      await defer.promise;
    } else {
      action(this.socket.server);
    }
  }
  private async sendPromise<T>(action: (server: IServer) => Promise<T>): Promise<T> {
    if (this.sending) {
      const defer = deferred<T>();
      this.sendingQueue.push({ action, defer });
      return defer.promise;
    } else {
      return action(this.socket.server);
    }
  }
  private async sendAsync<T>(action: (server: IServer) => Promise<T>): Promise<T> {
    if (this.sending) {
      const defer = deferred<T>();
      this.sendingQueue.push({ action, defer });
      return defer.promise;
    } else {
      this.sending = true;

      try {
        return await action(this.socket.server);
      } finally {
        setTimeout(() => this.commitQueuedSends(), 0);
      }
    }
  }
  private async commitQueuedSends() {
    while (this.sendingQueue.length) {
      const { action, defer } = this.sendingQueue.shift()!;

      try {
        defer.resolve(await action(this.socket.server));
      } catch (e) {
        defer.reject(e);
      }
    }

    this.sending = false;
  }
  clearQueuedSends() {
    this.sendingQueue.length = 0;
    this.sending = false;
    this.clearSendingChunksId++;
  }
  // Fallback file download (experimental)
  // TODO: this can be simplified if ag-sockets adds binary promise results
  private fileQueue = new Map<string, Deferred<ArrayBuffer>>();
  getFile(name: string): Promise<ArrayBuffer> {
    if (!this.isAnySocketConnected()) {
      return Promise.reject(new Error('Not connected'));
    }

    let defer = this.fileQueue.get(name);

    if (!defer) {
      defer = deferred<ArrayBuffer>();
      this.fileQueue.set(name, defer);
      this.server.quickAction(QuickAction.RequestFile, name);
    }

    return defer.promise;
  }
  getLayerImage = (url: string): CancellablePromise<ImageBitmap | HTMLImageElement> => {
    let cancelled = false;
    const promise = this.getFile(url)
      .then(arrayBuffer => {
        if (cancelled) throw new Error('Cancelled');
        return dataToImage(new Uint8Array(arrayBuffer));
      })
      .then(image => {
        if (cancelled) throw new Error('Cancelled');
        return image;
      }) as CancellablePromise<ImageBitmap | HTMLImageElement>;
    promise.cancel = () => { cancelled = true; };
    return promise;
  };
  fileContent(name: string, content: Uint8Array | undefined) {
    const defer = this.fileQueue.get(name);

    if (defer) {
      this.fileQueue.delete(name);

      if (content) {
        defer.resolve(content.buffer.slice(content.byteOffset, content.byteOffset + content.byteLength));
      } else {
        defer.reject(new Error('File not found'));
      }
    }
  }
  clearPendingFiles() {
    while (this.fileQueue.size) {
      this.fileQueue.forEach(defer => defer.reject(new Error('Disconnected')));
      this.fileQueue.clear();
    }
  }
  fetchFile = (url: string) => this.getFile(url);
  // Chat
  private _chatOpened = false;
  get chatOpened() {
    return this._chatOpened;
  }
  set chatOpened(value: boolean) {
    if (this._chatOpened !== value) {
      this._chatOpened = value;

      if (value) {
        dismissChatHelp();
      } else {
        this.message = undefined;
        this.messageQueue = [];
      }
    }
  }
  sendMessage(message: string) {
    message = message.substring(0, CHAT_MESSAGE_LENGTH_LIMIT);

    if (DEVELOPMENT && message === '/clienterror') {
      this.internalReportError('Test error', new Error('Test error'), { some: 'data' });
      return;
    }

    if (DEVELOPMENT && message === '/pencil') {
      this.server.debug('pencil', JSON.stringify(pencilLog));
      return;
    }

    if (DEVELOPMENT && message === '/screenshot') {
      this.hideDebug = true;
    }

    if (message && canChat(this)) {
      this.server.chat(this.connId, message);
    }
  }
  openChat() {
    if (this.chatOpened) {
      setTimeout(() => document.querySelector<HTMLInputElement>('.chat-box-input')?.focus(), 1);
    } else {
      this.chatOpened = true;
    }
  }
  closeChat() {
    this.chatOpened = false;
  }
  toggleChat() {
    if (this.chatOpened) {
      this.closeChat();
    } else {
      this.openChat();
    }
  }
  colorTaken(color: string): boolean {
    if (this.users.some(u => u.color === color)) return true;
    const drawing = this.editor?.drawing;
    return !!drawing && drawing.respectOfflineOwners && drawing.layers.some(l => !l.owner && l.layerOwner?.color === color);
  }
  trackEvent(eventName: Analytics, props?: any) {
    if (this.isConnected) {
      this.socket.server.trackEvent(eventName, props);
      if (typeof window !== 'undefined') window.Intercom?.('trackEvent', eventName, props);
    }
  }
  isOwnerOrAdmin(id: string) {
    if (id !== this.editor?.drawing.id) return false;
    return hasDrawingRole(this.user, 'admin');
  }
  allowedFolderProjectTeam({ folder, project, team }: { folder?: string; project?: string; team?: string; }) {
    // ignore team and project if user is not a member of this team
    if (team && !this.manage.team(team)) {
      team = undefined;
      project = undefined;
    }

    // ignore folder if user is not and owner
    if (!team && !project && folder && this.user.role !== 'owner') {
      folder = undefined;
    }

    return { folder, project, team };
  }
  startPresentationMode(partialData: Partial<PresentationModeStartState> = {}) {
    const { participantsUiHidden, invitedUsers = [], isForced = false, followingHostLocationEnforced, followingHostViewportEnforced } = partialData;
    this.updatePresentationMode({
      ...DEFAULT_PRESENTATION_MODE_STATE,
      participantsUiHidden,
      host: { uniqIds: [this.user.uniqId], name: this.user.name },
      hostViewFilter: this.editor?.viewFilter,
      isForced,
      followingHostLocationEnforced,
      followingHostViewportEnforced,
      drawingId: this.editor?.drawing._id,
      sequenceId: this.editor?.drawing.sequenceId,
    });
    this.server.quickAction<InvitePresentationViewers>(QuickAction.InvitePresentationViewers, { invitedUsers, isForced });
  }
  async presentationHostIsAFK() {
    this.server.quickAction(QuickAction.PresentatioHostIsAFK);
    const response = await this.modals.presentationActionModal({
      title: 'Are you still there?',
      timer: PRESENTATION_INACTIVE_HOST_MODAL_TIME,
      mode: PresentationActions.inactivePresentationHost
    });
    if (!response) {
      this.server.quickAction(QuickAction.TakeOverPresentation);
    }
    return response;
  }
  continuePresentationMode() {
    const { host, originalHost, viewersUniqIds = [] } = this.presentationModeState;
    if (host && originalHost?.name === this.user.name) {
      this.modals.presentationActionModal({
        title: 'Reclaim Presentation',
        mode: PresentationActions.reclaimPresentation,
        user: this.presentationModeState.host?.name
      }).then(response => {
        host.uniqIds.forEach(uniqueId => viewersUniqIds.push(uniqueId));
        if (response) {
          this.updatePresentationMode({
            host: { uniqIds: [this.user.uniqId], name: this.user.name },
            originalHost: undefined,
            viewersUniqIds
          });
          this.server.quickAction(QuickAction.ReclaimedPresentation);
          this.editor?.toast(toastService => toastService.notification({ message: 'Your are now presenting.' }));
        }
      }).catch(e => DEVELOPMENT && console.error(e));
    } else {
      const hostIds = this.getUserIdsByUniqId(this.user, true);
      this.updatePresentationMode({ host: { uniqIds: hostIds, name: this.user.name } });
      // Closing Presentation Modals
      this.server.quickAction(QuickAction.TakeOverPresentation);
    }
  }
  stopPresentationMode() {
    this.updatePresentationMode({ ...DEFAULT_PRESENTATION_MODE_STATE, takingOver: true });
    this.server.quickAction(QuickAction.EndPresentationViewersNotification);
    storageSetBoolean('isPresentationModeHost', false);
  }
  updatePresentationMode(state: Partial<PresentationModeState>) {
    if (!this.presentationModeState.sequenceId && this.editor?.drawing.sequenceId) {
      state.sequenceId = this.editor?.drawing.sequenceId;
    }
    this.server.quickAction<Partial<PresentationModeState>>(QuickAction.ModifyPresentationModeState, state);

    if (state.followingHostViewportEnforced && this.editor) {
      this.server.quickAction<PresentViewport>(QuickAction.PresentViewport, { view: this.editor.view });
    }
  }
  passPresenterRolePresentationMode(uniqId: string) {
    this.server.quickAction<string>(QuickAction.PassPresenterRole, uniqId);
  }
  setPresentationMode(state: PresentationModeState) {
    this.presentationModeState = state;
    if (this.isPresentationHost || storageGetBoolean('isPresentationModeHost')) {
      if (this.editor && this.isPresentationHost && this.presentationModeState.followingHostLocationEnforced) {
        this.server.quickAction<string>(QuickAction.PresentDrawing, this.editor.drawing.id);
      }
      storageSetBoolean('isPresentationModeHost', true, MAX_PRESENTATION_TIMEOUT);
    } else {
      storageSetBoolean('isPresentationModeHost', false);
    }
  }
  takeOverPresentation() {
    const { viewersUniqIds = [] } = this.presentationModeState;
    this.updatePresentationMode({
      host: { uniqIds: [this.user.uniqId], name: this.user.name },
      originalHost: this.presentationModeState.host,
      viewersUniqIds: viewersUniqIds.filter(u => u !== this.user.uniqId)
    });
    this.server.quickAction(QuickAction.TakeOverPresentation);
  }
  passOverPresentation() {
    const { viewersUniqIds: viewersClientIds = [], host } = this.presentationModeState;
    host?.uniqIds.forEach(uniqId => viewersClientIds.push(uniqId));
    this.updatePresentationMode({
      host: { uniqIds: [this.user.uniqId], name: this.user.name },
      viewersUniqIds: viewersClientIds.filter(u => u !== this.user.uniqId),
      takingOver: true
    });
    // Removing host status for other users
    this.server.quickAction(QuickAction.ReclaimedPresentation);
  }
  joinPresentation() {
    this.server.quickAction(QuickAction.JoinPresentationDrawing);
    if (this.editor) {
      copyViewport(this.viewBeforeJoiningPresentationMode.view, this.editor.view);
      this.viewBeforeJoiningPresentationMode.drawingId = this.editor.drawing.id!;
      fitViewportOnScreen(this.editor.view);
    }
  }
  goBackToPreviousViewPresnetationMode() {
    const { editor, viewBeforeJoiningPresentationMode } = this;
    if (editor && viewBeforeJoiningPresentationMode) {
      editor.toast(toastService => toastService.notification({
        message: 'Stay on Presenter’s view or go back to your original view.',
        actionText: 'Back',
        action: () => {
          const { drawingId, view } = viewBeforeJoiningPresentationMode;
          navigateToDrawing(this.manage.router, drawingId, 'presentation');
          matchFromOtherViewport(editor.view, view);
        },
        closable: false
      }));
    }
  }
  leavePresentation() {
    this.server.quickAction(QuickAction.LeavePresentationDrawing);
    this.goBackToPreviousViewPresnetationMode();
  }
  inviteToPresentation(uniqId: string) {
    this.server.quickAction<InvitePresentationViewers>(QuickAction.InvitePresentationViewers, { invitedUsers: [uniqId], isForced: !!this.presentationModeState.isForced });
  }
  kickFromPresentation(uniqIds: string[]) {
    const { viewersUniqIds = [] } = this.presentationModeState;
    this.updatePresentationMode({ viewersUniqIds: viewersUniqIds.filter(u => !uniqIds.includes(u)) });
  }
  updatePresentationCoPresenters(uniqId: string, isCoPresenter: boolean) {
    let { coPresentersUniqIds = [] } = this.presentationModeState;
    if (!isCoPresenter) {
      coPresentersUniqIds.push(uniqId);
    } else {
      coPresentersUniqIds = coPresentersUniqIds.filter(u => u !== uniqId);
    }
    this.presentationModeState.coPresentersUniqIds = coPresentersUniqIds;
    this.updatePresentationMode({ coPresentersUniqIds });
  }
  get isPresentationMode() {
    return this.activePresentation && (this.isPresentationViewer || this.isPresentationHost);
  }
  get activePresentation() {
    const { drawingId, sequenceId } = this.presentationModeState;
    const drawing = this.editor?.drawing;
    return this.presentationModeState.host !== undefined && (drawing?.sequenceId === sequenceId || drawing?._id === drawingId || !!drawing?.sequence.some(d => d._id === drawingId));
  }
  get isPresentationHost() {
    return this.activePresentation && (!!this.presentationModeState.host?.uniqIds?.includes(this.user.uniqId) || storageGetBoolean('isPresentationModeHost'));
  }
  get isPresentationForced() {
    return this.presentationModeState.isForced && !this.isPresentationHost;
  }
  get getPresentationViewers() {
    const { viewersUniqIds = [] } = this.presentationModeState;
    viewersUniqIds.forEach(v => {
      let inPresentationViewers = this.presentationViewers.find(u => u.uniqId === v);
      let user = this.getUserByUniqId(v, true) ?? inPresentationViewers;
      if (!user && this.user.uniqId === v) user = this.user;
      if (user && !inPresentationViewers && !this.presentationViewers.includes(user)) {
        this.presentationViewers.push(user);
      }
    });
    return this.presentationViewers.filter(v => viewersUniqIds.includes(v.uniqId)) || [];
  }
  get isPresentationViewer() {
    return !this.isPresentationHost && this.presentationModeState.viewersUniqIds?.includes(this.user.uniqId);
  }
  isPresentationCoPresenter(uniqId: string) {
    return this.presentationModeState.coPresentersUniqIds?.includes(uniqId) || this.presentationModeState.host?.uniqIds?.includes(uniqId);
  }
  async executeCommandById(id: string, params: CommandParams, source: ToolSource) {
    await this.commandService.executeCommandById(id, params, source);
  }

  // storage
  get isStorageLimitExceeded() {
    if (!IS_PORTAL || IS_HOSTED) return false;
    const team = this.manage.activeTeam() as any;
    let hasNoUsageLimitFeature = false;

    if (team) hasNoUsageLimitFeature = team.featureFlags?.includes(Feature.StorageNoUsageLimits);
    else hasNoUsageLimitFeature = this.user.featureFlags.has(Feature.StorageNoUsageLimits);

    return this.storageLimitExceeded && !hasNoUsageLimitFeature;
  }
}

export function canChat(model: Model) {
  return model.isConnected && !model.editor?.locked && !!model.editor?.drawing.id &&
    model.loaded === 1 && !model.failed;
}

export function userChanged(model: Model) {
  model.userChanged.next(model.user);

  if (model.editor) {
    initEditorBrushesAndShapes(model.editor, model.user, e => model.reportError('initEditorBrushesAndShapes failed', e));
  }

  if (!SERVER) {
    void initBrushesAndShapes();
  }
}

export async function sendLayerSnapshot(editor: Editor, layerId: number) {
  if (PENCIL_LOG) editor.model.server.debug('pencil', JSON.stringify(pencilLog));
  const layer = getLayer(editor.drawing, layerId);
  if (!layer) { DEVELOPMENT && console.error('No layer'); return; }
  const canvas = editor.renderer.getLayerSnapshot(layer);
  if (!canvas) { DEVELOPMENT && console.error('No snapshot'); return; }
  const blob = await canvasToBlob(canvas);
  const buffer = await blobToArrayBuffer(blob);
  if (!buffer) { DEVELOPMENT && console.error('No buffer'); return; }
  const data = new Uint8Array(buffer);
  await editor.model.sendLayerSnapshot(layer.id, data);
}

export function createClientBrowserInfo(): ClientBrowserInfo {
  const wnd = typeof window !== 'undefined' ? window : undefined;
  if (!wnd) return {};

  const info: ClientBrowserInfo = {
    viewportSize: `${wnd.innerHeight}x${wnd.innerWidth}`,
  };
  if (isMobile) info.touchEnabled = isMobile;
  if (hasKeyboard) info.keyboardAttached = true;
  if (hasPressure) info.tabletAttached = true;
  if (hasExtension()) {
    info.chromeExtension = true;
    info.tabletModel = getTabletModel();
  }
  if (navigator.language) info.browserLanguage = navigator.language;
  if (wnd?.screen?.width && wnd.screen.height) info.screenResolution = `${wnd.screen.height}x${wnd.screen.width}`;
  if (document.referrer) {
    try {
      const ref = new URL(document.referrer);
      if (ref.host !== location.host) {
        info.referrer = ref.href;
        info.referringDomain = ref.host;
      }
    } catch (e) {
      DEVELOPMENT && console.warn(e);
    }
  }

  return info;
}
